import { Box, Center, Flex, Link, Text, useDisclosure } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { FC, useContext, useState } from "react";
import { FormattedMessage } from "react-intl";

import {
  RanaAlert,
  RanaProjectSelect,
  RanaTable,
  TableFilterCheckbox,
  TrashIcon,
} from "@nens/ranalib";
import { useTableData } from "@nens/react/hooks";
import * as api from "@nens/threedi/openapi-client";

import SchematisationsSvg from "@/assets/schematisations.svg?url";

import { getSchematisationsApi } from "../../apiConfiguration";
import { APP_BASE_HREF } from "../../App";
import { ExplainSideColumn } from "../../components/ExplainSideColumn";
import { OrganisationContext } from "../../providers/OrganisationProvider";
import { useUserHasRole } from "../../queries/general";
import { ID } from "../../queries/queryClient";
import { useDeleteMultipleSchematisation } from "../../queries/schematisations";
import RemoveMultipleModal from "../RemoveMultipleModal";

import SchematisationsExcelButton from "./SchematisationsExcelButton";

export interface SchematisationRow extends api.Schematisation {
  uuid: string;
}

const SchematisationRows: FC<{ rows: SchematisationRow[] }> = ({ rows }) => {
  return rows.map((row) => {
    const { name, tags, lastUpdated, createdByFirstName, createdByLastName, latestRevisionNr } =
      row;
    const tagsInfo = tags !== undefined && tags.length > 0 ? tags : "-";
    const lastUpdatedInfo = lastUpdated?.toLocaleDateString() ?? "";
    const fullName = `${createdByFirstName} ${createdByLastName}`;

    return (
      <RanaTable.Row
        to={`${APP_BASE_HREF}/schematisations/${row.id}`}
        rowId={row.uuid}
        key={row.uuid}
      >
        <RanaTable.Cell>{name}</RanaTable.Cell>
        <RanaTable.Cell>{latestRevisionNr}</RanaTable.Cell>
        <RanaTable.Cell>{tagsInfo}</RanaTable.Cell>
        <RanaTable.Cell>{lastUpdatedInfo}</RanaTable.Cell>
        <RanaTable.Cell>{fullName}</RanaTable.Cell>
      </RanaTable.Row>
    );
  });
};

const SchematisationsList: FC = () => {
  const { organisation } = useContext(OrganisationContext);
  const {
    limit,
    offset,
    setPageSize,
    setPage,
    orderings,
    setOrdering,
    orderingString,
    page,
    pageSize,
    searchField,
    setSearchField,
    searchString,
    setSearchString,
  } = useTableData({
    initialSearchField: "name",
    initialOrderings: { last_updated: "za" },
    initialPageSize: 20,
  });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const userIsNotCreator = !useUserHasRole("creator");
  const [deleteIds, setDeleteIds] = useState<ID[]>([]);

  const [showNoRevisions, setShowNoRevisions] = useState<boolean>(false);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);

  const schematisationsApi = getSchematisationsApi();

  const schematisationsQuery = (limit: number, offset: number) =>
    schematisationsApi.schematisationsList({
      ownerUniqueId: organisation.uniqueId,
      limit,
      offset,
      nameIcontains: searchField === "name" && searchString ? searchString : undefined,
      tagsIn: searchField === "tag" && searchString ? searchString : undefined,
      createdByUsernameIcontains:
        searchField === "creator" && searchString ? searchString : undefined,
      ordering: orderingString,
      // If there is no selectedProjectId then we want to filter on projectId=null, which has
      // a slightly different syntax. ranaProjectId: null would filter on the *string* "null".
      ...(selectedProjectId ? { ranaProjectId: selectedProjectId } : { ranaProjectIdIsnull: true }),
      ...(!showNoRevisions && { lastUpdatedIsnull: false }),
    });

  const schematisations = useQuery({
    queryKey: [
      "schematisations",
      organisation.uniqueId,
      limit,
      offset,
      searchString,
      searchString ? searchField : null,
      orderingString,
      showNoRevisions,
      selectedProjectId,
    ],
    queryFn: () => schematisationsQuery(limit, offset),
  });

  const rows: SchematisationRow[] =
    schematisations.status === "success"
      ? schematisations.data.results.map((row) => {
          return { ...row, uuid: "" + row.id };
        })
      : [];

  return (
    <ExplainSideColumn
      imgUrl={SchematisationsSvg}
      imgAltDescription={"Schematisations icon"}
      headerText={"Schematisations"}
      explanationText={"Available schematisations. Click on one to see its revisions."}
      backUrl={"/"}
    >
      <Box mt={2}>
        <Box maxW="1500px">
          <RanaAlert status="info" mb={4}>
            <Box pl={4}>
              <Text as="strong">
                <FormattedMessage
                  id="schematisations.cannot_find_schematisation"
                  defaultMessage="Can't find your model schematisation?"
                />
              </Text>
              <Text>
                <FormattedMessage
                  id="schematisations.not_visible"
                  defaultMessage="If a model schematisation is <strong>not</strong> visible here, it is located in your project."
                  values={{
                    strong: (chunks) => <strong>{chunks}</strong>,
                  }}
                />
                <br />
                <FormattedMessage
                  id="schematisations.use_project_selector"
                  defaultMessage="Use the project selector below, choose your project to see the schematisation stored in your project."
                />
              </Text>
              <Text>
                <FormattedMessage
                  id="schematisations.more_info_user_manual"
                  defaultMessage=" More information can be found in the {user_manual}"
                  values={{
                    user_manual: (
                      <Link
                        textDecor="underline"
                        isExternal
                        href="https://docs.ranawaterintelligence.com/d_migration_guide_rana_hcc_catalogue.html"
                        color="brand.clear_creek"
                      >
                        <FormattedMessage
                          id="schematisation.user_manual"
                          defaultMessage="user manual"
                        />
                      </Link>
                    ),
                  }}
                />
              </Text>
            </Box>
          </RanaAlert>
        </Box>
        <Box maxW="400px" mb={4}>
          <RanaProjectSelect
            value={selectedProjectId}
            onChange={(projectId) => {
              setSelectedProjectId(projectId);
              setPage(1);
            }}
          />
        </Box>
        <Flex justifyContent="space-between" maxW="1500px">
          <TableFilterCheckbox
            value={showNoRevisions}
            onChange={setShowNoRevisions}
            id="no-revisions-checkbox"
          >
            Include schematisations with no revisions
          </TableFilterCheckbox>
          <SchematisationsExcelButton
            marginTop="4"
            marginLeft="4"
            organisationUniqueId={organisation.uniqueId}
            schematisationsQuery={schematisationsQuery}
          />
        </Flex>
        <RanaTable
          maxW="1500px"
          sx={{
            // vertical alignment for the icons and checkboxes
            "& p": {
              marginBottom: 0,
            },
            "& label": {
              marginBottom: 0,
            },
          }}
          columnDefinitions={[
            {
              width: "table_cell",
              title: "Name",
              orderingField: "name",
            },
            {
              width: "table_cell_xs",
              title: "Revision",
            },
            {
              width: "table_cell_xs",
              title: "Tag",
            },
            {
              width: "table_cell_xs",
              title: "Update",
              orderingField: "last_updated",
            },
            {
              width: "table_cell_xs",
              title: "Created by",
              orderingField: "created_by__username",
            },
          ]}
          emptyElement={
            <Center textAlign="center" w="full" h="full">
              There is no data to be shown with the current filters.
            </Center>
          }
          tableDataIds={rows.map((row) => row.uuid)}
          isLoading={schematisations.status !== "success"}
          ordering={orderings}
          setOrdering={setOrdering}
          tableActions={[
            {
              label: "Delete selected",
              icon: <TrashIcon boxSize={4} mr={3} />,
              isDisabled: userIsNotCreator,
              action: (ids) => {
                setDeleteIds(ids.map((id) => parseInt(id)));
                onOpen();
              },
            },
          ]}
        >
          <RanaTable.Filters
            search={{
              placeholder: "Type to search",
              searchString,
              setSearchString,
            }}
            dropdowns={[
              {
                label: "Search by",
                selected: searchField,
                setSelected: setSearchField,
                options: [
                  {
                    value: "name",
                    label: "Name",
                  },
                  {
                    value: "tag",
                    label: "Tag (exact)",
                  },
                  {
                    value: "creator",
                    label: "Created by",
                  },
                ],
              },
            ]}
          />
          <RanaTable.Container>
            <RanaTable.Header />
            <RanaTable.Body>
              <SchematisationRows rows={rows} />
            </RanaTable.Body>
          </RanaTable.Container>
          <RanaTable.Pagination
            numPages={Math.ceil((schematisations?.data?.count ?? 0) / pageSize)}
            {...{ limit, setPageSize, page, setPage }}
          />
        </RanaTable>
        <RemoveMultipleModal
          isOpen={isOpen}
          onClose={onClose}
          deleteMultipleFunction={useDeleteMultipleSchematisation(deleteIds)}
        >
          This deletes all the selected schematisations, all its revisions and their Rana models.
          This action cannot be undone.
        </RemoveMultipleModal>
      </Box>
    </ExplainSideColumn>
  );
};

export default SchematisationsList;
